/**
 * A vacancy's enums, in a recruiter's words.
 *
 * These lived inside `views/JobOverview.tsx` as file-local constants, which was
 * fine while one screen read them. It stopped being fine when the Discord alert
 * for an employer-submitted vacancy needed the same seven maps: a second copy is
 * how `hybrid` ends up printed as "Híbrido" on one surface and "Hibrido" on
 * another, and how a renamed option gets renamed in one place only.
 *
 * Deliberately in `config/` and deliberately free of imports, like
 * `employer_identity.ts` next to it: the admin panel bundles this into the
 * browser and the backend loads the same file in Node, and a shared vocabulary
 * that two runtimes cannot both reach is not shared.
 *
 * The ids are the `jobs` collection's enum ids and the labels are its labels —
 * `collections/jobs.ts` remains where the option *exists*; this is only how it
 * is spoken. Adding an option there and not here shows the raw id, which is the
 * intended failure: visible, and obviously unfinished.
 */

export const JOB_STATUS_LABEL: Record<string, string> = {
    pending_review: "Pendiente de revisión",
    published: "Publicada",
    // Sent back to the company with a note (drizzle/0066).
    returned: "Devuelta con nota",
    draft: "Borrador",
    closed: "Cerrada"
};

export const JOB_STATUS_COLOR: Record<string, string> = {
    pending_review: "orangeLighter",
    published: "greenLighter",
    returned: "purpleLighter",
    draft: "grayLighter",
    closed: "redLighter"
};

export const WORK_MODEL_LABEL: Record<string, string> = {
    on_site: "Presencial",
    remote: "Remoto",
    hybrid: "Híbrido"
};

export const EMPLOYMENT_LABEL: Record<string, string> = {
    full_time: "Tiempo completo",
    part_time: "Medio tiempo"
};

export const CONTRACT_LABEL: Record<string, string> = {
    permanent: "Indefinido",
    fixed_term: "Temporal (con fecha)",
    freelance: "Freelance",
    internship: "Prácticas",
    temporary: "Eventual"
};

export const SENIORITY_LABEL: Record<string, string> = {
    intern: "Pasante",
    junior: "Junior",
    mid: "Mid-Level",
    senior: "Senior",
    lead: "Lead",
    manager: "Manager",
    director: "Director",
    executive: "Ejecutivo / C-Level"
};

export const APPLICATION_METHOD_LABEL: Record<string, string> = {
    internal: "En SustenTalent",
    external: "En un sitio externo",
    email: "Por correo"
};

export const JOB_SOURCE_LABEL: Record<string, string> = {
    submission: "Publicada por la empresa",
    manual: "Cargada por el equipo",
    jsearch: "Importada · JSearch",
    serpapi: "Importada · SerpAPI",
    url_extract: "Importada · desde URL"
};

/**
 * One enum value, spoken.
 *
 * Falls back to the raw id rather than to a placeholder: an unmapped option is a
 * gap in this file, and printing `fixed_term` says so to whoever can fix it,
 * where "—" would just look like missing data on the vacancy.
 */
export function jobLabel(map: Record<string, string>, value: unknown): string | null {
    const id = value == null ? "" : String(value).trim();
    if (!id) return null;
    return map[id] ?? id;
}
